import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { BarChart3, TrendingUp, Users, Target, Award, HelpCircle, Loader2, Newspaper, Calendar, RefreshCw, ChevronLeft, ChevronRight, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, PlayCircle, Download, FileDown, Sparkles } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts';
import api from '../services/api';
import AIChat from '../components/AIChat';
import { useToast } from '../components/ui/toast';
import EnginePill from '../components/EnginePill';
import RecordedCitationHistory from '../components/RecordedCitationHistory';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mediaOutlets, setMediaOutlets] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [trendHistory, setTrendHistory] = useState([]);
  const [trendModel, setTrendModel] = useState('');
  const [trendVersion,setTrendVersion]=useState('geo-search-v2');
  const [trendView, setTrendView] = useState('history');
  const [trendInterval, setTrendInterval] = useState('weekly');
  const [trendDays, setTrendDays] = useState(90);
  const [shareOfVoice, setShareOfVoice] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [visibility, setVisibility] = useState(null);
  const [visibilityMode, setVisibilityMode] = useState('industry');
  const [keywordPage, setKeywordPage] = useState(0);
  const keywordsPerPage = 10;
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [brandedFilter, setBrandedFilter] = useState('all'); // 'all' | 'brand' | 'non-brand'
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [outletCitations, setOutletCitations] = useState([]);
  const [outletDialogOpen, setOutletDialogOpen] = useState(false);
  const [loadingOutlet, setLoadingOutlet] = useState(false);
  const [keywordSortField, setKeywordSortField] = useState('keyword');
  const [keywordSortDirection, setKeywordSortDirection] = useState('asc');
  const [visibleLines, setVisibleLines] = useState({
    totalCitations: true,
    ownedCitations: true,
    brandMentions: true,
    competitorCitations: true
  });

  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        const response = await api.get('/websites');
        setWebsites(response.data.websites);
        if (response.data.websites.length > 0) {
          setSelectedWebsite(response.data.websites[0].id);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch websites');
      }
    };

    fetchWebsites();
  }, []);

  // AI Executive Summary — fetched separately so it can refresh independently
  const [execSummary, setExecSummary] = useState(null);
  const summaryRequest = useRef(0);
  const [execSummaryLoading, setExecSummaryLoading] = useState(false);
  const [execSummaryError, setExecSummaryError] = useState('');

  // Sentiment drill-down dialog
  const [sentimentDialogOpen, setSentimentDialogOpen] = useState(false);
  const [sentimentDialogType, setSentimentDialogType] = useState(null);
  const [sentimentDialogScope, setSentimentDialogScope] = useState('all');
  const [sentimentDialogCitations, setSentimentDialogCitations] = useState(null);
  const [sentimentDialogLoading, setSentimentDialogLoading] = useState(false);

  const openSentimentDialog = async (sentiment, scope) => {
    setSentimentDialogType(sentiment);
    setSentimentDialogScope(scope);
    setSentimentDialogOpen(true);
    setSentimentDialogLoading(true);
    setSentimentDialogCitations(null);
    try {
      const resp = await api.get(`/dashboard/website/${selectedWebsite}/sentiment-citations?sentiment=${sentiment}&scope=${scope}`);
      setSentimentDialogCitations(resp.data);
    } catch (err) {
      setSentimentDialogCitations({ citations: [], total: 0, error: err.response?.data?.message || 'Failed to load' });
    } finally {
      setSentimentDialogLoading(false);
    }
  };

  const fetchExecSummary = async (websiteId, force = false) => {
    if (!websiteId) return;
    const requestId = ++summaryRequest.current;
    if (!force) setExecSummary(null);
    setExecSummaryLoading(true);
    setExecSummaryError('');
    try {
      const resp = await api[force ? 'post' : 'get'](`/dashboard/website/${websiteId}/executive-summary`);
      if (requestId === summaryRequest.current) setExecSummary(resp.data);
    } catch (err) {
      if (requestId === summaryRequest.current) setExecSummaryError(err.response?.data?.message || 'Failed to generate summary');
    } finally {
      if (requestId === summaryRequest.current) setExecSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (selectedWebsite) {
      fetchExecSummary(selectedWebsite);
    } else {
      setExecSummary(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWebsite]);

  useEffect(() => {
    if (!selectedWebsite) return;

    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedCategory !== 'all') params.set('category', selectedCategory);
        if (brandedFilter !== 'all') params.set('branded', brandedFilter);
        const dashQs = params.toString();
        const [dashboardResponse, mediaResponse, trendsResponse, categoriesResponse, sovResponse, coverageResponse, visibilityResponse] = await Promise.all([
          api.get(`/dashboard/website/${selectedWebsite}${dashQs ? '?' + dashQs : ''}`),
          api.get(`/websites/${selectedWebsite}/media-outlets?days=${trendDays}`),
          api.get(`/dashboard/trends/${selectedWebsite}?days=${trendDays}&interval=${trendInterval}`),
          api.get(`/websites/${selectedWebsite}/keyword-categories`),
          Promise.resolve({ data: null }),
          api.get(`/dashboard/keyword-coverage/${selectedWebsite}`),
          Promise.resolve({ data: null })
        ]);
        setData(dashboardResponse.data);
        setMediaOutlets(mediaResponse.data.mediaOutlets || []);
        setTrendData(trendsResponse.data.trends || []);
        setTrendHistory(trendsResponse.data.history || []);
        setTrendModel(trendsResponse.data.model || '');
        setTrendVersion(trendsResponse.data.measurementVersion||'geo-search-v2');
        setCategories(categoriesResponse.data.categories || []);
        setShareOfVoice(sovResponse.data);
        setCoverage(coverageResponse.data);
        setVisibility(visibilityResponse.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedWebsite, trendInterval, trendDays, selectedCategory, brandedFilter, visibilityMode]);

  const handleWebsiteChange = (value) => {
    setSelectedWebsite(value);
  };

  const exportToCSV = () => {
    if (!data) return;

    const selectedWebsiteData = websites.find(w => w.id === selectedWebsite);
    const websiteName = selectedWebsiteData?.brandName || 'Website';
    const timestamp = new Date().toISOString().split('T')[0];

    // Create CSV content
    let csvContent = `Careerminds Labs — GEO Dash Report - ${websiteName}\n`;
    csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;

    // Summary metrics
    csvContent += `SUMMARY METRICS\n`;
    csvContent += `Metric,Value\n`;
    csvContent += `Owned Citations,${data.ownedCitations || 0}\n`;
    csvContent += `Total Brand Mentions,${data.totalBrandMentions || 0}\n`;
    csvContent += `Brand Citation Score,${data.brandCitationScore || 0}%\n`;
    csvContent += `Competitor Citations,${data.competitorCitations || 0}\n`;
    csvContent += `Brand vs Competitor Share,${data.brandMentionShare || 0}%\n`;
    csvContent += `Total Citations,${data.totalCitations || 0}\n\n`;

    // Keyword performance
    csvContent += `KEYWORD PERFORMANCE\n`;
    csvContent += `Keyword,Owned Citations,Brand Mentions,Total Citations,Brand Score\n`;
    data.keywordStats.forEach(stat => {
      csvContent += `"${stat.keyword}",${stat.ownedCitations || 0},${stat.totalBrandMentions || 0},${stat.totalCitations || 0},${stat.brandScore || 0}%\n`;
    });
    csvContent += `\n`;

    // Top domains
    if (data.domainStats && data.domainStats.length > 0) {
      csvContent += `TOP CITING DOMAINS\n`;
      csvContent += `Domain,Citation Count\n`;
      data.domainStats.forEach(domain => {
        csvContent += `"${domain.domain}",${domain.count}\n`;
      });
    }

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `geo-report-${websiteName.toLowerCase().replace(/\s+/g, '-')}-${timestamp}.csv`;
    link.click();
    toast.success('CSV report downloaded successfully!');
  };

  const exportToJSON = () => {
    if (!data) return;

    const selectedWebsiteData = websites.find(w => w.id === selectedWebsite);
    const websiteName = selectedWebsiteData?.brandName || 'Website';
    const timestamp = new Date().toISOString().split('T')[0];

    const exportData = {
      website: {
        id: selectedWebsite,
        name: websiteName,
      },
      generated: new Date().toISOString(),
      summary: {
        ownedCitations: data.ownedCitations || 0,
        totalBrandMentions: data.totalBrandMentions || 0,
        brandCitationScore: data.brandCitationScore || 0,
        competitorCitations: data.competitorCitations || 0,
        brandMentionShare: data.brandMentionShare || 0,
        totalCitations: data.totalCitations || 0,
      },
      keywords: data.keywordStats,
      topDomains: data.domainStats,
      mediaOutlets: mediaOutlets,
      trends: trendData,
      recordedHistory: trendHistory,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `geo-report-${websiteName.toLowerCase().replace(/\s+/g, '-')}-${timestamp}.json`;
    link.click();
    toast.success('JSON report downloaded successfully!');
  };

  const handleOutletClick = async (outlet) => {
    setSelectedOutlet(outlet);
    setOutletDialogOpen(true);
    setLoadingOutlet(true);

    try {
      const response = await api.get(`/websites/${selectedWebsite}/media-outlets/${encodeURIComponent(outlet.domain)}/citations?days=30`);
      setOutletCitations(response.data.citations || []);
    } catch (err) {
      console.error('Error fetching outlet citations:', err);
      setOutletCitations([]);
    } finally {
      setLoadingOutlet(false);
    }
  };

  const handleKeywordSort = (field) => {
    if (keywordSortField === field) {
      // Toggle direction if clicking same field
      setKeywordSortDirection(keywordSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending default
      setKeywordSortField(field);
      setKeywordSortDirection('asc');
    }
  };

  const getSortedKeywords = () => {
    if (!data?.keywordStats) return [];

    const sorted = [...data.keywordStats].sort((a, b) => {
      let aVal, bVal;

      switch (keywordSortField) {
        case 'keyword':
          aVal = a.keyword.toLowerCase();
          bVal = b.keyword.toLowerCase();
          break;
        case 'ownedCitations':
          aVal = a.ownedCitations || 0;
          bVal = b.ownedCitations || 0;
          break;
        case 'totalBrandMentions':
          aVal = a.totalBrandMentions || 0;
          bVal = b.totalBrandMentions || 0;
          break;
        case 'brandCitations':
          aVal = a.brandCitations || 0;
          bVal = b.brandCitations || 0;
          break;
        case 'totalCitations':
          aVal = a.totalCitations || 0;
          bVal = b.totalCitations || 0;
          break;
        case 'brandScore':
          aVal = a.brandScore || 0;
          bVal = b.brandScore || 0;
          break;
        default:
          return 0;
      }

      if (keywordSortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return sorted;
  };

  const toggleLine = (dataKey) => {
    setVisibleLines(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mt-4">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data && selectedWebsite) {
    return (
      <TooltipProvider>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            {websites.length > 0 && (
              <div className="w-64">
                <Select value={selectedWebsite} onValueChange={setSelectedWebsite}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select website" />
                  </SelectTrigger>
                  <SelectContent>
                    {websites.map((website) => (
                      <SelectItem key={website.id} value={website.id}>
                        {website.brandName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <Alert className="mt-4">
            <AlertDescription>
              No citation data available for this website yet. Run some keyword queries to see results.
            </AlertDescription>
          </Alert>
        </div>
      </TooltipProvider>
    );
  }

  if (!selectedWebsite && websites.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <Alert className="mt-4">
          <AlertDescription>
            No websites found. Start by adding websites and keywords.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const selectedWebsiteData = websites.find(w => w.id === selectedWebsite);

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold tracking-tight">
                Dashboard
              </h1>
              <EnginePill />
            </div>
            {(data?.lastSuccessfulRunAt || data?.lastRunAttemptedAt || data?.recentFailureCount > 0) && (() => {
              const successAt = data.lastSuccessfulRunAt ? new Date(data.lastSuccessfulRunAt) : null;
              const attemptAt = data.lastRunAttemptedAt ? new Date(data.lastRunAttemptedAt) : null;
              // Prefer last successful run for the freshness signal; if there's never been one,
              // fall back to last attempt so the user knows the system is at least trying.
              const showAt = successAt || attemptAt;
              const isFallback = !successAt && !!attemptAt;
              const ageMs = showAt ? Date.now() - showAt.getTime() : 0;
              const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24));
              const ageHours = Math.floor(ageMs / (1000 * 60 * 60));
              const ageMins = Math.floor(ageMs / (1000 * 60));
              const ageLabel = !showAt
                ? 'No runs yet'
                : ageDays > 0 ? `${ageDays}d ago`
                : ageHours > 0 ? `${ageHours}h ago`
                : ageMins > 0 ? `${ageMins}m ago`
                : 'just now';
              const stale = ageDays >= 7;
              const label = isFallback ? 'Last attempt' : 'Last refresh';
              const dotColor = !showAt ? 'bg-slate-400'
                : isFallback ? 'bg-orange-500'
                : stale ? 'bg-amber-500'
                : 'bg-emerald-500';
              const textColor = isFallback ? 'text-orange-700'
                : stale ? 'text-amber-700'
                : 'text-foreground';
              return (
                <div className="flex items-center gap-3 mt-1.5 text-xs">
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                    {label}: <span className={`font-medium ${textColor}`}>{ageLabel}</span>
                    {isFallback && data.lastRunStatus && (
                      <span className="text-orange-700">({data.lastRunStatus})</span>
                    )}
                  </span>
                </div>
              );
            })()}
          </div>
          <div className="flex gap-3">
            {data && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Download className="h-4 w-4" />
                    Export Report
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportToCSV} className="gap-2">
                    <FileDown className="h-4 w-4" />
                    Download as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToJSON} className="gap-2">
                    <FileDown className="h-4 w-4" />
                    Download as JSON
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <div className="inline-flex items-center rounded-md border bg-background p-0.5 text-xs">
              {[
                { value: 'all', label: 'All' },
                { value: 'brand', label: 'Brand' },
                { value: 'non-brand', label: 'Non-brand' }
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setBrandedFilter(opt.value)}
                  className={`px-3 py-1.5 rounded transition-colors font-medium ${
                    brandedFilter === opt.value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {categories.length > 0 && (
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {websites.length > 0 && (
              <Select value={selectedWebsite} onValueChange={handleWebsiteChange}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select website" />
                </SelectTrigger>
                <SelectContent>
                  {websites.map((website) => (
                    <SelectItem key={website.id} value={website.id}>
                      {website.brandName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* AI Executive Summary — generated only on explicit refresh */}
        {selectedWebsite && (
          <Card className="border-primary/30 bg-gradient-to-br from-background to-primary/[0.03] shadow-md animate-in fade-in duration-500">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <CardTitle className="text-base">AI Executive Summary</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fetchExecSummary(selectedWebsite, true)}
                  disabled={execSummaryLoading}
                  className="h-7 text-xs"
                >
                  {execSummaryLoading ? (
                    <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Thinking…</>
                  ) : (
                    <><RefreshCw className="h-3 w-3 mr-1" /> Refresh</>
                  )}
                </Button>
              </div>
              <CardDescription className="text-xs">
                Updates only when you select Refresh. {execSummary?.generatedAt
                  ? <>Last updated: <time dateTime={new Date(execSummary.generatedAt).toISOString()}>{new Date(execSummary.generatedAt).toLocaleString()}</time>.</>
                  : 'Not generated yet.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {execSummaryError ? (
                <p className="text-sm text-red-700">{execSummaryError}</p>
              ) : null}
              {execSummaryLoading && !execSummary ? (
                <div className="space-y-2">
                  <div className="h-3 w-full bg-muted rounded animate-pulse" />
                  <div className="h-3 w-5/6 bg-muted rounded animate-pulse" />
                  <div className="h-3 w-4/6 bg-muted rounded animate-pulse" />
                </div>
              ) : execSummary?.summary?.length > 0 ? (
                <ul className="space-y-2">
                  {execSummary.summary.map((bullet, i) => (
                    <li key={i} className="flex gap-2 text-sm">
                      <span className="text-primary mt-0.5">•</span>
                      <span className="leading-relaxed">{bullet}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No summary available yet.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* KPI Cards with Tooltips */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 animate-in fade-in duration-500">
          <Card className="transition-all duration-200 hover:shadow-lg hover:shadow-green-500/10 hover:scale-[1.02] shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                Owned Citations
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="inline-flex">
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Citations from your own domain(s) in LLM responses</p>
                  </TooltipContent>
                </Tooltip>
              </CardTitle>
              <Award className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {data.ownedCitations || 0}
              </div>
            </CardContent>
          </Card>

          <Card className="transition-all duration-200 hover:shadow-lg hover:shadow-emerald-500/10 hover:scale-[1.02] shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                Total Brand Mentions
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="inline-flex">
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Total times your brand name is mentioned across all citations</p>
                  </TooltipContent>
                </Tooltip>
              </CardTitle>
              <Award className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                {data.totalBrandMentions || 0}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {data.brandMentionCitations || 0} citation{data.brandMentionCitations !== 1 ? 's' : ''}
              </p>
            </CardContent>
          </Card>

          <Card className="transition-all duration-200 hover:shadow-lg hover:shadow-emerald-500/10 hover:scale-[1.02] shadow-md md:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                Brand Sentiment
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="inline-flex">
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>How AI responses frame your brand across citations that actually mention it. Run the sentiment backfill in Settings if older citations are still unclassified.</p>
                  </TooltipContent>
                </Tooltip>
              </CardTitle>
              <Sparkles className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {(() => {
                const brandMix = data.sentimentMix?.brand;
                const allMix = data.sentimentMix?.all;
                if (!brandMix && !allMix) return <p className="text-xs text-muted-foreground">No data yet.</p>;
                const brandTotal = brandMix ? brandMix.positive + brandMix.neutral + brandMix.negative : 0;
                const allTotal = allMix ? allMix.positive + allMix.neutral + allMix.negative : 0;
                // Prefer the brand-specific view; fall back to all-citations view so the card
                // still shows useful sentiment data for sites where most citations are 'other'.
                const useBrand = brandTotal > 0;
                const mix = useBrand ? brandMix : allMix;
                const total = useBrand ? brandTotal : allTotal;
                const scope = useBrand ? 'across brand-mentioning citations' : 'across all citations (no brand-mention citations yet)';
                if (total === 0) {
                  if ((mix?.unclassified || 0) > 0) {
                    return <p className="text-xs text-muted-foreground">{mix.unclassified} citation{mix.unclassified === 1 ? '' : 's'} unclassified — run sentiment backfill in Settings.</p>;
                  }
                  return <p className="text-xs text-muted-foreground">No citations yet.</p>;
                }
                const pct = (n) => total > 0 ? (n / total) * 100 : 0;
                return (
                  <div className="space-y-3">
                    <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
                      {mix.positive > 0 && (
                        <div className="bg-emerald-500" style={{ width: `${pct(mix.positive)}%` }} />
                      )}
                      {mix.neutral > 0 && (
                        <div className="bg-slate-400" style={{ width: `${pct(mix.neutral)}%` }} />
                      )}
                      {mix.negative > 0 && (
                        <div className="bg-red-500" style={{ width: `${pct(mix.negative)}%` }} />
                      )}
                    </div>
                    <div className="flex justify-between gap-4 text-xs">
                      <button
                        type="button"
                        onClick={() => mix.positive > 0 && openSentimentDialog('positive', useBrand ? 'brand' : 'all')}
                        className={`flex items-center gap-1.5 rounded px-1 py-0.5 ${mix.positive > 0 ? 'hover:bg-emerald-50 cursor-pointer' : 'cursor-default'}`}
                        disabled={mix.positive === 0}
                      >
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        <span className="font-medium">{mix.positive}</span>
                        <span className="text-muted-foreground">positive ({Math.round(pct(mix.positive))}%)</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => mix.neutral > 0 && openSentimentDialog('neutral', useBrand ? 'brand' : 'all')}
                        className={`flex items-center gap-1.5 rounded px-1 py-0.5 ${mix.neutral > 0 ? 'hover:bg-slate-100 cursor-pointer' : 'cursor-default'}`}
                        disabled={mix.neutral === 0}
                      >
                        <span className="h-2 w-2 rounded-full bg-slate-400" />
                        <span className="font-medium">{mix.neutral}</span>
                        <span className="text-muted-foreground">neutral ({Math.round(pct(mix.neutral))}%)</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => mix.negative > 0 && openSentimentDialog('negative', useBrand ? 'brand' : 'all')}
                        className={`flex items-center gap-1.5 rounded px-1 py-0.5 ${mix.negative > 0 ? 'hover:bg-red-50 cursor-pointer' : 'cursor-default'}`}
                        disabled={mix.negative === 0}
                      >
                        <span className="h-2 w-2 rounded-full bg-red-500" />
                        <span className="font-medium">{mix.negative}</span>
                        <span className="text-muted-foreground">negative ({Math.round(pct(mix.negative))}%)</span>
                      </button>
                      {mix.unclassified > 0 && (
                        <div className="text-muted-foreground">+{mix.unclassified} unclassified</div>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground">{scope} · click any number to see the citations</p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          <Card className="transition-all duration-200 hover:shadow-lg hover:shadow-purple-500/10 hover:scale-[1.02] shadow-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                Brand vs Competitor Share
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="inline-flex">
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Your brand's share of total citations vs competitors. Higher percentage means better competitive position</p>
                  </TooltipContent>
                </Tooltip>
              </CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {data.brandMentionShare || 0}%
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Industry Visibility — chart + ranking table, modeled on the Tesla/Hyundai reference */}
        {visibility && visibility.ranking && visibility.ranking.length > 0 && (() => {
          const palette = ['#3b82f6', '#f97316', '#8b5cf6', '#10b981', '#ec4899'];
          const ownDomainRow = visibility.ranking.find(r => r.classification === 'owned');
          const ownDomain = ownDomainRow ? ownDomainRow.domain : null;
          const lineDomains = visibility.topDomains || [];
          const lineColor = (idx, domain) => domain === ownDomain ? '#3b82f6' : palette[(idx + 1) % palette.length];
          const classBadge = (cls) => {
            if (cls === 'owned') return { label: 'You', cls: 'bg-blue-100 text-blue-700 border-blue-200' };
            if (cls === 'competitor') return { label: 'Competitor', cls: 'bg-orange-100 text-orange-700 border-orange-200' };
            if (cls === 'brand_mention') return { label: 'Brand mention', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
            return { label: 'Other', cls: 'bg-slate-100 text-slate-600 border-slate-200' };
          };
          const rankDelta = (curr, prev) => {
            if (!prev || prev === 0) return { text: 'New', color: 'text-emerald-600' };
            const d = prev - curr; // positive = improved
            if (d === 0) return { text: '=', color: 'text-muted-foreground' };
            if (d > 0) return { text: `↑${d}`, color: 'text-emerald-600' };
            return { text: `↓${-d}`, color: 'text-orange-600' };
          };
          return (
            <Card className="mb-6 transition-all duration-200 hover:shadow-lg shadow-md animate-in fade-in slide-in-from-bottom-4 duration-700">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Visibility
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button className="inline-flex">
                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p><strong>Industry only</strong> shows brands and competitors we recognise. <strong>All sources</strong> includes media, forums and reference sites — the full picture of who AI is citing for your topics.</p>
                        </TooltipContent>
                      </Tooltip>
                    </CardTitle>
                    <CardDescription>Last 90 days · weekly · {visibilityMode === 'industry' ? 'industry sources' : 'all sources'}</CardDescription>
                  </div>
                  <div className="inline-flex rounded-md border bg-muted/30 p-0.5">
                    <button
                      onClick={() => setVisibilityMode('industry')}
                      className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${visibilityMode === 'industry' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      Industry only
                    </button>
                    <button
                      onClick={() => setVisibilityMode('all')}
                      className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${visibilityMode === 'all' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      All sources
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                  {/* Left: line chart */}
                  <div className="lg:col-span-3 h-[340px]">
                    {visibility.series && visibility.series.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={visibility.series} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="period" tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }} />
                          <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }} />
                          <RechartsTooltip
                            formatter={(value, name) => [`${value}%`, name]}
                            contentStyle={{
                              backgroundColor: 'hsl(var(--popover))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '6px'
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          {lineDomains.map((d, idx) => (
                            <Line
                              key={d.domain}
                              type="monotone"
                              dataKey={d.domain}
                              stroke={lineColor(idx, d.domain)}
                              strokeWidth={d.domain === ownDomain ? 3 : 2}
                              dot={{ r: d.domain === ownDomain ? 4 : 3 }}
                              connectNulls
                              name={d.domain}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <p className="text-muted-foreground text-sm">Not enough data for a time series in this window.</p>
                      </div>
                    )}
                  </div>
                  {/* Right: ranking table */}
                  <div className="lg:col-span-2">
                    <div className="text-sm font-semibold mb-2">Industry ranking</div>
                    <div className="text-xs text-muted-foreground mb-3">Sources with the highest visibility</div>
                    <div className="overflow-hidden border rounded-md">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10 text-xs">#</TableHead>
                            <TableHead className="text-xs">Source</TableHead>
                            <TableHead className="text-xs text-right">Visibility</TableHead>
                            <TableHead className="text-xs text-right">Prev rank</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {visibility.ranking.slice(0, 8).map((row) => {
                            const badge = classBadge(row.classification);
                            const rd = rankDelta(row.currentRank, row.previousRank);
                            const isOwn = row.classification === 'owned';
                            return (
                              <TableRow key={`${row.domain}-${row.currentRank}`} className={isOwn ? 'bg-blue-50/50' : ''}>
                                <TableCell className="font-medium tabular-nums">{row.currentRank}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <span className={`truncate max-w-[140px] ${isOwn ? 'font-semibold' : ''}`}>{row.domain}</span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${badge.cls}`}>{badge.label}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  <span className="font-medium">{row.currentPct}%</span>
                                  {Math.abs(row.deltaPct) >= 0.1 && (
                                    <span className={`ml-1 text-xs ${row.deltaPct > 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                                      {row.deltaPct > 0 ? '↗' : '↘'}{Math.abs(row.deltaPct).toFixed(1)}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  <span className={`text-xs font-medium ${rd.color}`}>{rd.text}</span>
                                  {row.previousRank > 0 && row.previousRank !== row.currentRank && (
                                    <span className="text-[10px] text-muted-foreground ml-1">#{row.previousRank}</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Keyword Coverage */}
        {coverage && coverage.totalKeywords > 0 && (() => {
          const winning = coverage.keywordsWinning || 0;
          const contested = coverage.keywordsContested || 0;
          const losing = coverage.keywordsLosing || 0;
          const brandOnly = Math.max(0, (coverage.keywordsWithBrand || 0) - winning - contested);
          const absent = Math.max(0, coverage.totalKeywords - winning - contested - losing - brandOnly);
          const total = coverage.totalKeywords || 1;
          const segments = [
            { label: 'Winning', count: winning, color: 'bg-emerald-500', textColor: 'text-emerald-700' },
            { label: 'Brand only', count: brandOnly, color: 'bg-blue-500', textColor: 'text-blue-700' },
            { label: 'Contested', count: contested, color: 'bg-amber-500', textColor: 'text-amber-700' },
            { label: 'Losing', count: losing, color: 'bg-orange-500', textColor: 'text-orange-700' },
            { label: 'Brand absent', count: absent, color: 'bg-slate-300', textColor: 'text-slate-600' }
          ];
          return (
            <Card className="mb-6 transition-all duration-200 hover:shadow-lg shadow-md animate-in fade-in slide-in-from-bottom-4 duration-700">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Keyword Coverage
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button className="inline-flex">
                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>For each tracked keyword we sum brand vs competitor citations. <strong>Winning</strong> = more brand than competitor. <strong>Contested</strong> = tied. <strong>Losing</strong> = competitor ahead. <strong>Brand absent</strong> = brand never cited.</p>
                        </TooltipContent>
                      </Tooltip>
                    </CardTitle>
                    <CardDescription>
                      {coverage.keywordsWithBrand} of {coverage.totalKeywords} tracked keywords show your brand
                      {coverage.keywordsWithBrand > 0 ? ` (${Math.round((coverage.keywordsWithBrand / total) * 100)}%)` : ''}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">Winning</div>
                      <div className="text-3xl font-bold text-emerald-600">{winning}<span className="text-lg text-muted-foreground font-normal">/{coverage.totalKeywords}</span></div>
                    </div>
                    {losing > 0 && (
                      <div className="text-right border-l pl-6">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">Losing</div>
                        <div className="text-3xl font-bold text-orange-600">{losing}<span className="text-lg text-muted-foreground font-normal">/{coverage.totalKeywords}</span></div>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex h-3 rounded-full overflow-hidden mb-3">
                  {segments.map((seg) => seg.count > 0 && (
                    <Tooltip key={seg.label}>
                      <TooltipTrigger asChild>
                        <div
                          className={`${seg.color} transition-all hover:opacity-80`}
                          style={{ width: `${(seg.count / total) * 100}%` }}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{seg.label}: {seg.count} keyword{seg.count !== 1 ? 's' : ''} ({Math.round((seg.count / total) * 100)}%)</p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
                  {segments.map((seg) => (
                    <div key={seg.label} className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-sm ${seg.color}`} />
                      <span className={`font-medium ${seg.textColor}`}>{seg.count}</span>
                      <span className="text-muted-foreground">{seg.label}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Share of Voice */}
        {shareOfVoice && shareOfVoice.series && shareOfVoice.series.length > 0 && (
          <Card className="mb-6 transition-all duration-200 hover:shadow-lg shadow-md animate-in fade-in slide-in-from-bottom-4 duration-700">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Share of Voice
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="inline-flex">
                          <HelpCircle className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Of the AI citations that mention your brand <em>or</em> a competitor, what percentage went to you. Excludes generic media and forum content.</p>
                      </TooltipContent>
                    </Tooltip>
                  </CardTitle>
                  <CardDescription>Last 90 days, weekly</CardDescription>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Your share (90d)</div>
                    <div className="text-3xl font-bold text-blue-600">
                      {shareOfVoice.summary.windowShare}%
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {shareOfVoice.summary.windowBrand} brand · {shareOfVoice.summary.windowCompetitor} competitor
                    </div>
                  </div>
                  {Math.abs(shareOfVoice.summary.delta) > 0.1 && (
                    <div className="text-right border-l pl-6">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">vs prior week</div>
                      <div className={`text-2xl font-bold flex items-center justify-end gap-1 ${shareOfVoice.summary.delta > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                        {shareOfVoice.summary.delta > 0 ? <ArrowUp className="h-5 w-5" /> : <ArrowDown className="h-5 w-5" />}
                        {Math.abs(shareOfVoice.summary.delta).toFixed(1)}pp
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={shareOfVoice.series}
                      stackOffset="expand"
                      margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="period"
                        tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                      />
                      <YAxis
                        tickFormatter={(v) => `${Math.round(v * 100)}%`}
                        tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                      />
                      <RechartsTooltip
                        formatter={(value, name, item) => {
                          const total = (item?.payload?.brandCitations || 0) + (item?.payload?.competitorCitations || 0);
                          const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                          return [`${value} (${pct}%)`, name];
                        }}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '6px'
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="brandCitations"
                        stackId="1"
                        stroke="#3b82f6"
                        fill="#3b82f6"
                        fillOpacity={0.85}
                        name="Your brand"
                      />
                      <Area
                        type="monotone"
                        dataKey="competitorCitations"
                        stackId="1"
                        stroke="#f97316"
                        fill="#f97316"
                        fillOpacity={0.7}
                        name="Competitors"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <div className="text-sm font-semibold mb-3">Top competing domains</div>
                  {shareOfVoice.topCompetitors && shareOfVoice.topCompetitors.length > 0 ? (
                    <div className="space-y-2">
                      {shareOfVoice.topCompetitors.map((c) => {
                        const max = shareOfVoice.topCompetitors[0].citations || 1;
                        const pct = (c.citations / max) * 100;
                        return (
                          <div key={c.domain} className="text-sm">
                            <div className="flex justify-between mb-1">
                              <span className="truncate pr-2">{c.domain}</span>
                              <span className="text-muted-foreground tabular-nums">{c.citations}</span>
                            </div>
                            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-orange-500 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No competitor citations detected in this window.</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Citation Trends Chart */}
        <Card className="mb-6 transition-all duration-200 hover:shadow-lg shadow-md animate-in fade-in slide-in-from-bottom-4 duration-700">
          <CardHeader>
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Citation Trends
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="inline-flex">
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Track your citation performance over time. Monitor brand vs competitor citations and overall trends.</p>
                    </TooltipContent>
                  </Tooltip>
                </CardTitle>
                <CardDescription>
                  {trendView === 'history'
                    ? `Saved citation history over the last ${trendDays} days, including earlier tracking.`
                    : `Current model detail over the last ${trendDays} days. Choose All saved history to include earlier tracking.`}
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                <Select value={trendView} onValueChange={setTrendView}>
                  <SelectTrigger className="w-48" aria-label="Citation history view"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="history">All saved history</SelectItem>
                    <SelectItem value="current">Current model detail</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={trendDays.toString()} onValueChange={(value) => setTrendDays(parseInt(value))}>
                  <SelectTrigger className="w-32" aria-label="Citation history date range">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 Days</SelectItem>
                    <SelectItem value="60">60 Days</SelectItem>
                    <SelectItem value="90">90 Days</SelectItem>
                    <SelectItem value="180">180 Days</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={trendInterval} onValueChange={setTrendInterval}>
                  <SelectTrigger className="w-32" aria-label="Citation history interval">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {trendView === 'history' ? (
              <RecordedCitationHistory rows={trendHistory} currentModel={trendModel} currentVersion={trendVersion} />
            ) : trendData && trendData.length > 0 ? (
              <div className="space-y-4">
                <div className="flex justify-center flex-wrap gap-6">
                  <button
                    onClick={() => toggleLine('totalCitations')}
                    className={`flex items-center gap-2 px-3 py-1 rounded-md transition-all ${
                      visibleLines.totalCitations
                        ? 'bg-slate-100 hover:bg-slate-200'
                        : 'bg-gray-100 hover:bg-gray-200 opacity-50'
                    }`}
                  >
                    <div className={`w-8 h-1 rounded ${visibleLines.totalCitations ? 'bg-slate-600' : 'bg-gray-400'}`} />
                    <span className="text-sm font-medium">Total Citations</span>
                  </button>
                  <button
                    onClick={() => toggleLine('ownedCitations')}
                    className={`flex items-center gap-2 px-3 py-1 rounded-md transition-all ${
                      visibleLines.ownedCitations
                        ? 'bg-green-100 hover:bg-green-200'
                        : 'bg-gray-100 hover:bg-gray-200 opacity-50'
                    }`}
                  >
                    <div className={`w-8 h-1 rounded ${visibleLines.ownedCitations ? 'bg-green-600' : 'bg-gray-400'}`} />
                    <span className="text-sm font-medium">Owned Citations</span>
                  </button>
                  <button
                    onClick={() => toggleLine('brandMentions')}
                    className={`flex items-center gap-2 px-3 py-1 rounded-md transition-all ${
                      visibleLines.brandMentions
                        ? 'bg-blue-100 hover:bg-blue-200'
                        : 'bg-gray-100 hover:bg-gray-200 opacity-50'
                    }`}
                  >
                    <div className={`w-8 h-1 rounded ${visibleLines.brandMentions ? 'bg-blue-600' : 'bg-gray-400'}`} />
                    <span className="text-sm font-medium">Brand Mentions</span>
                  </button>
                  <button
                    onClick={() => toggleLine('competitorCitations')}
                    className={`flex items-center gap-2 px-3 py-1 rounded-md transition-all ${
                      visibleLines.competitorCitations
                        ? 'bg-orange-100 hover:bg-orange-200'
                        : 'bg-gray-100 hover:bg-gray-200 opacity-50'
                    }`}
                  >
                    <div className={`w-8 h-1 rounded ${visibleLines.competitorCitations ? 'bg-orange-600' : 'bg-gray-400'}`} />
                    <span className="text-sm font-medium">Competitor Citations</span>
                  </button>
                </div>
                <div className="h-[320px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={trendData}
                      margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="period"
                        tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                        tickLine={{ stroke: 'hsl(var(--border))' }}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                        tickLine={{ stroke: 'hsl(var(--border))' }}
                        axisLine={{ stroke: 'hsl(var(--border))' }}
                      />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '6px'
                        }}
                        labelStyle={{ color: 'hsl(var(--foreground))' }}
                      />
                      {visibleLines.totalCitations && (
                        <Line
                          type="monotone"
                          dataKey="totalCitations"
                          stroke="#475569"
                          strokeWidth={2}
                          strokeDasharray="6 4"
                          name="Total Citations"
                          dot={{ r: 4 }}
                        />
                      )}
                      {visibleLines.ownedCitations && (
                        <Line
                          type="monotone"
                          dataKey="ownedCitations"
                          stroke="#10b981"
                          strokeWidth={3}
                          name="Owned Citations"
                          dot={{ r: 5 }}
                        />
                      )}
                      {visibleLines.brandMentions && (
                        <Line
                          type="monotone"
                          dataKey="brandMentions"
                          stroke="#3b82f6"
                          strokeWidth={3}
                          name="Brand Mentions"
                          dot={{ r: 5 }}
                        />
                      )}
                      {visibleLines.competitorCitations && (
                        <Line
                          type="monotone"
                          dataKey="competitorCitations"
                          stroke="#f59e0b"
                          strokeWidth={3}
                          name="Competitor Citations"
                          dot={{ r: 5 }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center h-[320px]">
                <p className="text-muted-foreground">The new model baseline has no completed keyword runs yet. Historical citations remain available above.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
          {/* Keyword Performance Table */}
          <Card className="flex flex-col transition-all duration-200 hover:shadow-lg shadow-md">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Target className="h-5 w-5" />
                Keyword Performance
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="inline-flex">
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Compare brand citations vs total citations for each keyword. Higher brand citations indicate better LLM ranking for those terms</p>
                  </TooltipContent>
                </Tooltip>
              </CardTitle>
              <CardDescription>
                Brand citation performance across different keywords
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 pb-6">
              {data.keywordStats && data.keywordStats.length > 0 ? (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleKeywordSort('keyword')}
                          >
                            <div className="flex items-center gap-1">
                              Keyword
                              {keywordSortField === 'keyword' ? (
                                keywordSortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                              ) : (
                                <ArrowUpDown className="h-3 w-3 opacity-50" />
                              )}
                            </div>
                          </TableHead>
                          <TableHead
                            className="text-right cursor-pointer hover:bg-muted/50"
                            onClick={() => handleKeywordSort('ownedCitations')}
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center justify-end gap-1">
                                  Owned
                                  <HelpCircle className="h-3 w-3 text-muted-foreground" />
                                  {keywordSortField === 'ownedCitations' ? (
                                    keywordSortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                  ) : (
                                    <ArrowUpDown className="h-3 w-3 opacity-50" />
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Citations from your own domain(s)</p>
                              </TooltipContent>
                            </Tooltip>
                          </TableHead>
                          <TableHead
                            className="text-right cursor-pointer hover:bg-muted/50"
                            onClick={() => handleKeywordSort('totalBrandMentions')}
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center justify-end gap-1">
                                  Mentions
                                  <HelpCircle className="h-3 w-3 text-muted-foreground" />
                                  {keywordSortField === 'totalBrandMentions' ? (
                                    keywordSortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                  ) : (
                                    <ArrowUpDown className="h-3 w-3 opacity-50" />
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Total times your brand name appears in citations</p>
                              </TooltipContent>
                            </Tooltip>
                          </TableHead>
                          <TableHead
                            className="text-right cursor-pointer hover:bg-muted/50"
                            onClick={() => handleKeywordSort('totalCitations')}
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center justify-end gap-1">
                                  Total
                                  <HelpCircle className="h-3 w-3 text-muted-foreground" />
                                  {keywordSortField === 'totalCitations' ? (
                                    keywordSortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                  ) : (
                                    <ArrowUpDown className="h-3 w-3 opacity-50" />
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Total citations in LLM responses for this keyword</p>
                              </TooltipContent>
                            </Tooltip>
                          </TableHead>
                          <TableHead
                            className="text-right cursor-pointer hover:bg-muted/50"
                            onClick={() => handleKeywordSort('brandScore')}
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center justify-end gap-1">
                                  Brand Score
                                  <HelpCircle className="h-3 w-3 text-muted-foreground" />
                                  {keywordSortField === 'brandScore' ? (
                                    keywordSortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                  ) : (
                                    <ArrowUpDown className="h-3 w-3 opacity-50" />
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Percentage of citations that mention your brand (owned + mentions / total citations)</p>
                              </TooltipContent>
                            </Tooltip>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getSortedKeywords()
                          .slice(keywordPage * keywordsPerPage, (keywordPage + 1) * keywordsPerPage)
                          .map((stat) => (
                            <TableRow key={stat.id} className="transition-colors hover:bg-muted/50">
                              <TableCell className="font-medium">
                                <button
                                  onClick={() => navigate(`/keyword/${stat.id}`)}
                                  className="text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {stat.keyword}
                                </button>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className="text-green-600 font-semibold">
                                  {stat.ownedCitations || 0}
                                </span>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className="text-emerald-600 font-semibold">
                                  {stat.totalBrandMentions || 0}
                                </span>
                              </TableCell>
                              <TableCell className="text-right">{stat.totalCitations}</TableCell>
                              <TableCell className="text-right">
                                <span className={stat.brandScore >= 50 ? 'text-green-600 font-semibold' : stat.brandScore >= 25 ? 'text-orange-600' : 'text-red-600'}>
                                  {stat.brandScore}%
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                  {getSortedKeywords().length > keywordsPerPage && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-muted-foreground">
                        Showing {keywordPage * keywordsPerPage + 1} to {Math.min((keywordPage + 1) * keywordsPerPage, getSortedKeywords().length)} of {getSortedKeywords().length} keywords
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setKeywordPage(Math.max(0, keywordPage - 1))}
                          disabled={keywordPage === 0}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setKeywordPage(keywordPage + 1)}
                          disabled={(keywordPage + 1) * keywordsPerPage >= getSortedKeywords().length}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex justify-center items-center py-8">
                  <p className="text-muted-foreground">No keyword data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Media Outlets Table */}
        <Card className="transition-all duration-200 hover:shadow-lg shadow-md animate-in fade-in slide-in-from-bottom-4 duration-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Newspaper className="h-5 w-5" />
                  Top Media Outlets in LLM Responses
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="inline-flex">
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>News outlets and publications that appear most frequently in LLM responses for your keywords. Use this to identify which media outlets to prioritize for PR and outreach.</p>
                    </TooltipContent>
                  </Tooltip>
                </CardTitle>
                <CardDescription>
                  Most cited media outlets across all keyword searches (last 30 days)
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {mediaOutlets && mediaOutlets.length > 0 ? (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Media Outlet</TableHead>
                      <TableHead className="text-right">
                        <Tooltip>
                          <TooltipTrigger>
                            <span className="flex items-center justify-end gap-1">
                              Total Citations
                              <HelpCircle className="h-3 w-3" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Times this outlet appeared in LLM responses</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableHead>
                      <TableHead className="text-right">
                        <Tooltip>
                          <TooltipTrigger>
                            <span className="flex items-center justify-end gap-1">
                              Your Brand
                              <HelpCircle className="h-3 w-3" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Times this outlet cited YOUR brand</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableHead>
                      <TableHead className="text-right">Confidence</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mediaOutlets.map((outlet) => (
                      <TableRow
                        key={outlet.domain}
                        className="cursor-pointer transition-all duration-150 hover:bg-muted/50 hover:shadow-sm"
                        onClick={() => handleOutletClick(outlet)}
                      >
                        <TableCell className="font-medium">{outlet.domain}</TableCell>
                        <TableCell className="text-right">{outlet.totalCitations || outlet.count}</TableCell>
                        <TableCell className="text-right">
                          <span className={outlet.brandCitations > 0 ? 'text-green-600 font-semibold' : ''}>
                            {outlet.brandCitations || 0}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {(outlet.avgConfidence * 100).toFixed(0)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex justify-center items-center py-8">
                <p className="text-muted-foreground">No media outlet citations found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Chat Assistant */}
        {selectedWebsite && <AIChat websiteId={selectedWebsite} />}

        {/* Media Outlet Detail Dialog */}
        <Dialog open={outletDialogOpen} onOpenChange={setOutletDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Citations from {selectedOutlet?.domain}</DialogTitle>
              <DialogDescription>
                Individual pages and articles citing your keywords (last 30 days)
              </DialogDescription>
            </DialogHeader>

            {loadingOutlet ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : outletCitations.length > 0 ? (
              <div className="space-y-4">
                <div className="flex gap-4 text-sm text-muted-foreground mb-4">
                  <div>
                    <span className="font-semibold text-foreground">{outletCitations.length}</span> total citations
                  </div>
                  <div>
                    <span className="font-semibold text-green-600">
                      {outletCitations.filter(c => c.brand_classification === 'brand').length}
                    </span> brand citations
                  </div>
                </div>

                <div className="space-y-3">
                  {outletCitations.map((citation) => (
                    <div
                      key={citation.id}
                      className={`border rounded-lg p-4 ${
                        citation.brand_classification === 'brand' ? 'border-green-500 bg-green-50/50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <a
                              href={citation.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-semibold text-primary hover:underline flex items-center gap-1"
                            >
                              {citation.title || 'Untitled'}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {citation.snippet}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {citation.brand_classification === 'brand' && (
                            <Badge variant="success" className="text-xs">
                              Brand Citation
                            </Badge>
                          )}
                          {citation.brand_classification === 'competitor' && (
                            <Badge variant="destructive" className="text-xs">
                              Competitor
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            Position: {citation.position}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Seen {citation.seen_count} time{citation.seen_count !== 1 ? 's' : ''}</span>
                        <span>Last: {new Date(citation.last_seen).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center py-8">
                <p className="text-muted-foreground">No citations found for this outlet</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Sentiment drill-down dialog */}
        <Dialog open={sentimentDialogOpen} onOpenChange={setSentimentDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className={`h-3 w-3 rounded-full ${
                  sentimentDialogType === 'positive' ? 'bg-emerald-500'
                  : sentimentDialogType === 'negative' ? 'bg-red-500'
                  : 'bg-slate-400'
                }`} />
                {sentimentDialogType ? sentimentDialogType.charAt(0).toUpperCase() + sentimentDialogType.slice(1) : ''} sentiment citations
              </DialogTitle>
              <DialogDescription>
                {sentimentDialogScope === 'brand' ? 'Citations that mention your brand' : 'All citations'} classified as {sentimentDialogType}.
                {sentimentDialogCitations?.total > sentimentDialogCitations?.shown ? ` Showing top ${sentimentDialogCitations.shown} of ${sentimentDialogCitations.total}.` : ''}
              </DialogDescription>
            </DialogHeader>
            {sentimentDialogLoading ? (
              <div className="py-8 flex items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
              </div>
            ) : sentimentDialogCitations?.error ? (
              <p className="text-sm text-red-700">{sentimentDialogCitations.error}</p>
            ) : sentimentDialogCitations?.citations?.length > 0 ? (
              <div className="space-y-3">
                {sentimentDialogCitations.citations.map(c => (
                  <div key={c.id} className="rounded-md border p-3 hover:bg-muted/30 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-sm hover:text-primary line-clamp-1 flex-1"
                      >
                        {c.title || c.url}
                      </a>
                      {c.keyword && (
                        <Badge variant="outline" className="text-[10px] shrink-0">{c.keyword}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{c.domain}</p>
                    {c.snippet && (
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-3 italic">"{c.snippet}"</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">No citations found.</p>
            )}
          </DialogContent>
        </Dialog>

        {/* Top cited URLs on this website's own domain — which of OUR pages
            ChatGPT is citing, and how often. */}
        {data?.topOwnedUrls?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="h-5 w-5" />
                Your most-cited pages
              </CardTitle>
              <CardDescription>
                Pages on {data.website?.domain || 'your domain'} that ChatGPT cites for your tracked keywords. Use this to see which existing content is winning in AI search.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="pb-2 font-medium">URL</th>
                      <th className="pb-2 font-medium w-32">Keywords</th>
                      <th className="pb-2 font-medium w-32 text-right">Times cited</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topOwnedUrls.slice(0, 20).map((u, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 pr-4 max-w-md">
                          <a
                            href={u.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline truncate block"
                            title={u.url}
                          >
                            {u.title || u.url}
                          </a>
                          {u.title && (
                            <p className="text-xs text-muted-foreground truncate" title={u.url}>{u.url}</p>
                          )}
                        </td>
                        <td className="py-2">
                          <span className="text-xs text-muted-foreground">{u.keywordCount}</span>
                        </td>
                        <td className="py-2 text-right">
                          <Badge variant="secondary">{u.citationCount}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
};

export default Dashboard;
